// src/composables/useStoreCache.ts
import { Ref, ref } from 'vue'
import { cacheDB, getTTLForStore } from '@/plugins/storeCachePlugin'
import { DateTime } from 'luxon'

export type StoreCacheEntryType = 'item' | 'collection' | 'draft' | string

const memoryCache = new Map<string, Ref<unknown>>()

const DIRECT_LOGGING = (_id: string, _input: unknown) => {
  // if (id == 'business-partner-store.gen:collection:{"query":{"itemsPerPage":6,"code[]":["10321","10000","10344","11055","11231","10301"],"page":1}}') {
  //   return true
  // }
  return false
}
export type StoreCache = ReturnType<typeof useStoreCache>

export function useStoreCache(namespace: string) {
  function makeKey(input: unknown): string {
    return typeof input === 'string' ? input : JSON.stringify(input)
  }

  async function cleanExpired() {
    // const open = await cacheDB.isOpen()
    // debugger
    // if (!open) await cacheDB.open()
    // debugger
    // await cacheDB.cache.where('store').anyOf(['irgendwas']).primaryKeys().then(console.log)
    //
    // debugger
    // const storeEntries = cacheDB.cache.where('store').equals(namespace)
    // debugger
    // const keyPromise = storeEntries.toArray()
    // debugger
    // const expired = await keyPromise
    // debugger
    // const expired2 = await storeEntries.primaryKeys() //returned niemals??
    // debugger
    // // await cacheDB.cache.bulkDelete(expired)
    // debugger
  }

  async function get<T>(input: unknown, type: StoreCacheEntryType): Promise<T | undefined> {
    const key = makeKey(input)
    const id = `${namespace}:${type}:${key}`
    const entry = await cacheDB.cache.get(id)
    if (!entry) {
      if (DIRECT_LOGGING(id, input)) console.log(`${id} cache miss 1`)
      return undefined
    }
    if (Date.now() > entry.updatedAt + entry.ttl) {
      await cacheDB.cache.delete(id)
      if (DIRECT_LOGGING(id, input)) console.log(`${id} cache expired`)
      return undefined
    }

    if (DIRECT_LOGGING(id, input)) console.log(`${id} hit`)
    return JSON.parse(entry.value)
  }

  async function set<T>(input: unknown, value: T, type: StoreCacheEntryType, ttlOverride?: number) {
    const key = makeKey(input)
    const id = `${namespace}:${type}:${key}`
    const ttl = ttlOverride ?? getTTLForStore(namespace)
    const now = Date.now()
    if (memoryCache.has(id)) (memoryCache.get(id) as Ref<T>).value = value
    else memoryCache.set(id, ref(value))
    await cacheDB.cache.put({
      id,
      store: namespace,
      type,
      key,
      value: JSON.stringify(value),
      updatedAt: now,
      ttl
    })
    if (DIRECT_LOGGING(id, input)) console.log(`${id} added to cache (val: ${JSON.stringify(value)})`)
  }

  const pending = new Map<string, Promise<Ref<unknown>>>()

  async function getOrCreate<T>(input: unknown, factory: () => Promise<T>, type: StoreCacheEntryType, ttlOverride?: number): Promise<Ref<T>> {
    const key = makeKey(input)
    const id = `${namespace}:${type}:${key}`

    if (memoryCache.has(id)) {
      if (DIRECT_LOGGING(id, input)) console.log(`${id} served from memory cache`)
      return memoryCache.get(id) as Ref<T>
    }

    if (pending.has(id)) {
      if (DIRECT_LOGGING(id, input)) console.log(`${id} waiting for pending factory`)
      return pending.get(id) as Promise<Ref<T>>
    }

    const p = (async () => {
      const cached = await get<T>(input, type)
      if (cached !== undefined) {
        if (DIRECT_LOGGING(id, input)) console.log(`${id} cache hit`)
        const r = ref(cached)
        memoryCache.set(id, r)
        return r
      } else {
        const result = await factory()
        const r = ref(result)
        if (DIRECT_LOGGING(id, input)) console.log(`${id} cache miss`)
        if (result) await set(input, result, type, ttlOverride)
        memoryCache.set(id, r)
        return r
      }
    })()

    pending.set(id, p)
    try {
      const result = await p
      return result
    } finally {
      pending.delete(id)
    }
  }

  async function getRawEntry(input: unknown, type: StoreCacheEntryType) {
    const key = makeKey(input)
    const id = `${namespace}:${type}:${key}`

    const entry = await cacheDB.cache.get(id)
    if (!entry) return undefined

    if (Date.now() > entry.updatedAt + entry.ttl) {
      await cacheDB.cache.delete(id)
      return undefined
    }

    return entry
  }

  async function getMeta(input: unknown, type: StoreCacheEntryType) {
    const entry = await getRawEntry(input, type)
    if (!entry) return undefined

    return {
      id: entry.id,
      store: entry.store,
      type: entry.type,
      key: entry.key,
      updatedAt: entry.updatedAt,
      updatedAtDate: DateTime.fromMillis(entry.updatedAt),
      ttl: entry.ttl,
      expiresAt: entry.updatedAt + entry.ttl,
      expiresAtDate: DateTime.fromMillis(entry.updatedAt + entry.ttl)
    }
  }

  return {
    cleanExpired,
    get,
    set,
    getOrCreate,
    async delete(input: unknown, type: StoreCacheEntryType) {
      const key = makeKey(input)
      const id = `${namespace}:${type}:${key}`
      memoryCache.delete(id)
      await cacheDB.cache.delete(id)
    },
    async clear() {
      memoryCache.clear()
      await cacheDB.cache.where('store').equals(namespace).delete()
    },
    reset() {
      memoryCache.clear()
    },
    async hasData() {
      return (await cacheDB.cache.where('store').equals(namespace).count()) > 0
    },
    getMeta
  }
}
